import { useState, useEffect } from 'react';
import { t, getLocale } from '@/i18n';
import { getStatusStyle } from '@/lib/utils';
import { Monitor, X } from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { KpiCard } from '@/components/dashboard/KpiCard';
import { AlertStrip } from '@/components/dashboard/AlertStrip';
import { KpiDrilldownDialog } from '@/components/dashboard/KpiDrilldownDialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  ComposedChart, Bar, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { useCountUp } from '@/hooks/useCountUp';
import { useSimulation } from '@/hooks/useSimulation';
import { Sun, Sunset, Moon } from 'lucide-react';
import { costBreakdown } from '@/lib/costing';
import { ArrivalsSummary } from '@/components/dashboard/ArrivalsSummary';
import { initialLoadings, countByState } from '@/data/arrivalsData';

// Loadings come from the arrivals data so the dashboard tells the same story as /kamiony.
const arrivalCounts = countByState(initialLoadings);
const loadingsTotal = initialLoadings.length;
const loadingsDone = arrivalCounts.loaded;

const GlassTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const dayKey = `mock.day.${label}`;
  const displayLabel = typeof label === 'string' && /^(Po|Út|St|Čt|Pá|So|Ne|mock\.line[123])$/.test(label) ? t(label.startsWith('mock.') ? label : dayKey) : label;
  return (
    <div className="bg-white/90 backdrop-blur-md border border-border rounded-xl shadow-lg px-4 py-3">
      <p className="text-xs text-muted-foreground font-medium">{displayLabel}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-bold" style={{ color: p.color || 'hsl(var(--chart-1))' }}>
          {p.name}: {typeof p.value === 'number' ? p.value.toLocaleString() : p.value}
        </p>
      ))}
    </div>
  );
};

export default function Dashboard() {
  const { dashboard, dismissAlert } = useSimulation();
  const { kpis, productionWeek, lineUtilization, shiftData, alerts } = dashboard;

  const [shift, setShift] = useState('all');
  const [drilldown, setDrilldown] = useState(null);
  const [showMobileWarning, setShowMobileWarning] = useState(() => !sessionStorage.getItem('mobileWarningDismissed'));

  // Shift-adjusted output
  const shiftOutput = shift === 'all'
    ? kpis.todayOutput.value
    : shiftData[shift]?.output || 0;
  const shiftTarget = shift === 'all'
    ? kpis.todayOutput.target
    : shiftData[shift]?.target || 0;

  const heroTons = useCountUp(shiftOutput, { duration: 1400, delay: 300 });
  const heroLoadings = useCountUp(loadingsDone, { duration: 1000, delay: 500 });

  const shiftLabel = shift === 'all' ? t('dashboard.totalOutputToday')
    : shift === 'morning' ? `${t('dashboard.morningShift')} (${shiftData.morning.hours})`
    : shift === 'afternoon' ? `${t('dashboard.afternoonShift')} (${shiftData.afternoon.hours})`
    : `${t('dashboard.nightShift')} (${shiftData.night.hours})`;

  return (
    <PageLayout title={t('dashboard.title')} concept>
      <div className="space-y-6">
        {/* Mobile warning */}
        {showMobileWarning && (
          <div className="lg:hidden flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800 animate-fade-in-up">
            <Monitor className="h-4 w-4 shrink-0" />
            <span>{t('dash.mobileWarning')}</span>
            <button
              onClick={() => { setShowMobileWarning(false); sessionStorage.setItem('mobileWarningDismissed', '1'); }}
              className="ml-auto shrink-0 p-1 rounded hover:bg-amber-100"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {/* Arrivals first: what the client is solving now */}
        <ArrivalsSummary counts={arrivalCounts} total={loadingsTotal} />

        {/* Hero Banner */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary to-chart-1 p-5 sm:p-8 text-white">
          <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-white/5" />
          <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-white/5" />
          <div className="absolute top-1/2 right-1/4 w-64 h-64 rounded-full bg-white/[0.02]" />

          <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="text-sm text-white/70 font-medium mb-1">
                {new Date().toLocaleDateString(getLocale(), { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
              <h2 className="text-2xl sm:text-3xl font-bold mb-2">{t('dashboard.plant')}</h2>
              <p className="text-white/80 text-sm">{shiftLabel}</p>
              <div className="text-3xl sm:text-5xl font-extrabold mt-1 tracking-tight" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {heroTons.toLocaleString()} <span className="text-lg sm:text-2xl font-medium text-white/70">/ {shiftTarget.toLocaleString()} t</span>
              </div>
            </div>
            <div className="sm:text-right space-y-3">
              {/* Shift Toggle */}
              <ToggleGroup type="single" value={shift} onValueChange={v => v && setShift(v)} className="bg-white/10 rounded-lg p-1">
                <ToggleGroupItem value="all" className="text-xs text-white/80 data-[state=on]:bg-white/20 data-[state=on]:text-white h-7 px-2.5">{t('dashboard.all')}</ToggleGroupItem>
                <ToggleGroupItem value="morning" className="text-xs text-white/80 data-[state=on]:bg-white/20 data-[state=on]:text-white h-7 px-2.5">
                  <Sun className="h-3 w-3 mr-1" /> {t('dashboard.am')}
                </ToggleGroupItem>
                <ToggleGroupItem value="afternoon" className="text-xs text-white/80 data-[state=on]:bg-white/20 data-[state=on]:text-white h-7 px-2.5">
                  <Sunset className="h-3 w-3 mr-1" /> {t('dashboard.pm')}
                </ToggleGroupItem>
                <ToggleGroupItem value="night" className="text-xs text-white/80 data-[state=on]:bg-white/20 data-[state=on]:text-white h-7 px-2.5">
                  <Moon className="h-3 w-3 mr-1" /> {t('dashboard.night')}
                </ToggleGroupItem>
              </ToggleGroup>

              <div className="flex items-center gap-2 justify-end">
                <span className="w-2.5 h-2.5 bg-green-400 rounded-full status-pulse-green" />
                <span className="text-sm font-medium">{t('dashboard.operationsActive')}</span>
              </div>
              <div className="text-sm text-white/60">{`3 ${t('dashboard.linesInOperation')}`}</div>
              <div className="text-sm text-white/60" style={{ fontVariantNumeric: 'tabular-nums' }}>{heroLoadings} / {loadingsTotal} {t('dashboard.loadings')}</div>
            </div>
          </div>
        </div>

        {/* 7 KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-4">
          <div style={{ animationDelay: '100ms' }} className="animate-fade-in-up opacity-0">
            <KpiCard
              label={t('dashboard.todaysOutput')}
              value={`${kpis.todayOutput.value.toLocaleString()} t`}
              subtitle={`${kpis.todayOutput.trend > 0 ? '+' : ''}${kpis.todayOutput.trend}% ${t('dashboard.vsYesterday')}`}
              trend={kpis.todayOutput.trend > 0 ? 'up' : kpis.todayOutput.trend < 0 ? 'down' : 'neutral'}
              target={`${kpis.todayOutput.target.toLocaleString()} t`}
              onClick={() => setDrilldown('todayOutput')}
            />
          </div>
          <div style={{ animationDelay: '150ms' }} className="animate-fade-in-up opacity-0">
            <KpiCard
              label={t('dashboard.plantUtilization')}
              value={`${kpis.utilization.value}%`}
              subtitle={`3 ${t('dashboard.linesActive')}`}
              trend="neutral"
              target={`${kpis.utilization.target}%`}
              onClick={() => setDrilldown('utilization')}
            />
          </div>
          <div style={{ animationDelay: '200ms' }} className="animate-fade-in-up opacity-0">
            <KpiCard
              label={t('dashboard.oee')}
              value={kpis.oee.value}
              subtitle={`${kpis.oee.trend > 0 ? '+' : ''}${kpis.oee.trend}%`}
              trend={kpis.oee.trend > 0 ? 'up' : 'down'}
              target={kpis.oee.target}
              variant="oee"
              onClick={() => setDrilldown('oee')}
            />
          </div>
          <div style={{ animationDelay: '250ms' }} className="animate-fade-in-up opacity-0">
            <KpiCard
              label={t('dashboard.loadingsToday')}
              value={`${loadingsDone} / ${loadingsTotal}`}
              subtitle={`${loadingsTotal - loadingsDone} ${t('dashboard.remaining')}`}
              trend="neutral"
              onClick={() => setDrilldown('loadings')}
            />
          </div>
          <div style={{ animationDelay: '300ms' }} className="animate-fade-in-up opacity-0">
            <KpiCard
              label={t('dashboard.qualityRate')}
              value={`${kpis.qualityRate.value}%`}
              subtitle={`${kpis.qualityRate.trend > 0 ? '+' : ''}${kpis.qualityRate.trend}%`}
              trend={kpis.qualityRate.trend > 0 ? 'up' : kpis.qualityRate.trend < 0 ? 'down' : 'neutral'}
              target={`${kpis.qualityRate.target}%`}
              onClick={() => setDrilldown('qualityRate')}
            />
          </div>
          <div style={{ animationDelay: '350ms' }} className="animate-fade-in-up opacity-0">
            <KpiCard
              label={t('dashboard.energyCost')}
              value={`${(kpis.energyCost.value / 1000).toFixed(1)}k CZK`}
              subtitle={`${kpis.energyCost.trend}% vs avg`}
              trend={kpis.energyCost.trend < 0 ? 'up' : 'down'}
              target={`${(kpis.energyCost.target / 1000).toFixed(0)}k CZK`}
              variant="energy"
              onClick={() => setDrilldown('energyCost')}
            />
          </div>
          <div style={{ animationDelay: '400ms' }} className="animate-fade-in-up opacity-0">
            {(() => {
              const cb = costBreakdown();
              return (
                <KpiCard
                  label={t('dashboard.costPerTonne')}
                  value={`${cb.costKc} CZK/t`}
                  subtitle={t('dashboard.costPerTonneSub')}
                  trend={cb.tariff.type === 'peak' ? 'down' : cb.tariff.type === 'negative' ? 'up' : 'neutral'}
                  target={cb.equation}
                  variant="energy"
                />
              );
            })()}
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <Card className="lg:col-span-3 min-h-[360px] animate-fade-in-up opacity-0" style={{ animationDelay: '400ms' }}>
            <CardHeader>
              <CardTitle>{t('dashboard.productionLast7Days')}</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={productionWeek}>
                  <defs>
                    <linearGradient id="productionGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--chart-1))" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} />
                  <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => t(`mock.day.${v}`)} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                  <Tooltip content={<GlassTooltip />} />
                  <Bar dataKey="actual" fill="hsl(var(--chart-1))" radius={[6, 6, 0, 0]} name={t('dashboard.actual')} opacity={0.8} />
                  <Line type="monotone" dataKey="target" stroke="hsl(23, 81%, 52%)" strokeWidth={2} strokeDasharray="6 3" dot={false} name={t('dashboard.target')} />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="lg:col-span-2 min-h-[360px] animate-fade-in-up opacity-0" style={{ animationDelay: '500ms' }}>
            <CardHeader>
              <CardTitle>{t('dashboard.lineUtilization')}</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={lineUtilization} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} />
                  <XAxis type="number" domain={[0, 100]} fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis dataKey="name" type="category" fontSize={12} tickLine={false} axisLine={false} width={60} tickFormatter={(v) => t(v)} />
                  <Tooltip content={<GlassTooltip />} />
                  <Bar dataKey="utilization" fill="hsl(23, 81%, 52%)" radius={[0, 6, 6, 0]} name={t('dashboard.utilization')} opacity={0.8} barSize={20} />
                  {lineUtilization[0]?.target && lineUtilization.map((line, i) => (
                    <ReferenceLine key={i} x={line.target} stroke="hsl(var(--chart-1))" strokeDasharray="4 3" strokeWidth={1.5} />
                  ))}
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Hodnocení výroby */}
        <Card className=" animate-fade-in-up opacity-0" style={{ animationDelay: '550ms' }}>
          <CardHeader>
            <CardTitle>{t('dash.productionScorecard')}</CardTitle>
            <p className="text-xs text-muted-foreground">{t('dash.productionScorecardDesc')}</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { label: t('dashboard.todaysOutput'), value: kpis.todayOutput.value, target: kpis.todayOutput.target, unit: 't', pct: Math.round((kpis.todayOutput.value / kpis.todayOutput.target) * 100) },
                { label: 'OEE', value: kpis.oee.value, target: kpis.oee.target, unit: '%', pct: Math.round((parseFloat(kpis.oee.value) / parseFloat(kpis.oee.target)) * 100) },
                { label: t('dashboard.plantUtilization'), value: kpis.utilization.value, target: kpis.utilization.target, unit: '%', pct: Math.round((kpis.utilization.value / kpis.utilization.target) * 100) },
                { label: t('dashboard.qualityRate'), value: kpis.qualityRate.value, target: kpis.qualityRate.target, unit: '%', pct: Math.round((kpis.qualityRate.value / kpis.qualityRate.target) * 100) },
              ].map((metric) => {
                const status = getStatusStyle(metric.pct);
                return (
                  <div key={metric.label} className={`rounded-xl p-4 ${status.bg} space-y-2`}>
                    <div className="text-xs font-medium text-muted-foreground">{metric.label}</div>
                    <div className="text-2xl font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {typeof metric.value === 'number' ? metric.value.toLocaleString() : metric.value}{metric.unit}
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                      <div className={`h-full rounded-full ${status.bar} transition-all duration-500`} style={{ width: `${Math.min(metric.pct, 100)}%` }} />
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className={`font-semibold ${status.text}`}>{status.label}</span>
                      <span className="text-muted-foreground">{t('kpi.target')}: {metric.target}{metric.unit}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Alerts */}
        <div>
          <h3 className="text-sm font-semibold mb-3">{t('dashboard.activeAlerts')}</h3>
          <AlertStrip alerts={alerts} onDismiss={dismissAlert} />
        </div>
      </div>

      {/* Drill-down Dialog */}
      <KpiDrilldownDialog
        open={!!drilldown}
        onOpenChange={(open) => !open && setDrilldown(null)}
        title={
          drilldown === 'todayOutput' ? t('dashboard.todaysOutput') :
          drilldown === 'utilization' ? t('dashboard.plantUtilization') :
          drilldown === 'oee' ? t('dashboard.oeeFull') :
          drilldown === 'loadings' ? t('dashboard.loadingsToday') :
          drilldown === 'qualityRate' ? t('dashboard.qualityRate') :
          drilldown === 'energyCost' ? t('dashboard.energyCost') : ''
        }
        kpiKey={drilldown}
        dashboardData={dashboard}
      />
    </PageLayout>
  );
}
